import { Trash2, Upload } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useWidgetSettings } from '../../hooks/useWidgetSettings';
import { supabase } from '../../lib/supabase';
import { Switch } from '../ui/Switch';

export function WidgetControls() {
  const { user } = useAuth();
  const { chatbotPublicId } = useChatbot();
  const { settings, updateSettings, updateScriptSettings, loading, initialized, initializeSettings } = useWidgetSettings();
  
  // Local state for the welcome message to prevent losing focus
  const [welcomeMessage, setWelcomeMessage] = useState(settings.welcomeMessage);

  // Update local state when settings change from the server
  useEffect(() => {
    setWelcomeMessage(settings.welcomeMessage);
  }, [settings.welcomeMessage]);

  // Initialize settings when component mounts
  useEffect(() => {
    if (chatbotPublicId) {
      initializeSettings(chatbotPublicId);
    }
  }, [chatbotPublicId, initialized, initializeSettings]);

  const handleUploadIcon = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!user || !file || !chatbotPublicId) return;

    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `widgets/${chatbotPublicId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('chatbot-assets')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('chatbot-assets')
        .getPublicUrl(filePath);

      if (chatbotPublicId) {
        await updateSettings({ customIcon: publicUrl }, chatbotPublicId);
      }
    } catch (err) {
      console.error('Error uploading icon:', err);
      // Display error to user
      alert(`Error uploading icon: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleRemoveIcon = async () => {
    if (!chatbotPublicId) return;
    await updateSettings({ customIcon: null }, chatbotPublicId);
  };

  // Handle the welcome message input changes
  const handleWelcomeMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Update local state immediately to maintain responsiveness
    setWelcomeMessage(e.target.value);
  };

  // Only update the server when the user has finished typing
  const handleWelcomeMessageBlur = () => {
    if (chatbotPublicId && welcomeMessage !== settings.welcomeMessage) {
      updateSettings({ welcomeMessage }, chatbotPublicId);
    }
  };

  // Code d'intégration compact
  const integrationCode = `<script src="https://botmaker-agency.ai/web-component-widget.js"></script>`;

  // Code d'intégration avec options
  const integrationCodeWithOptions = `<script src="https://botmaker-agency.ai/web-component-widget.js"></script>
<script>
  document.addEventListener('DOMContentLoaded', () => {
    const botmaker = document.createElement('botmaker-chat');
    botmaker.setAttribute('bot-id', '${settings.publicId}');
    botmaker.setAttribute('position', '${settings.position}');
    botmaker.setAttribute('lang', '${settings.lang}');
    if (${settings.showWelcomeMessage}) botmaker.setAttribute('show-welcome', 'true');
    if (${settings.showNotificationBubble}) botmaker.setAttribute('show-notification', 'true');
    if (${settings.enableAnimation}) botmaker.setAttribute('enable-animation', 'true');
    document.body.appendChild(botmaker);
  });
</script>`;

  // Code d'intégration déclarative (HTML)
  const declarativeCode = `<script src="https://botmaker-agency.ai/web-component-widget.js"></script>
<botmaker-chat 
  bot-id="${settings.publicId}" 
  position="${settings.position}" 
  lang="${settings.lang}"
  ${settings.showWelcomeMessage ? 'show-welcome="true"' : ''}
  ${settings.showNotificationBubble ? 'show-notification="true"' : ''}
  ${settings.enableAnimation ? 'enable-animation="true"' : ''}
></botmaker-chat>`;

  // Générer l'URL de la page de test
  const testPageUrl = `https://botmaker-agency.ai/widget-test?id=${settings.publicId}`;

  return (
    <div className="space-y-6">
      {/* Paramètres d'affichage */}
      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Display settings</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-gray-300">Welcome message</label>
            <Switch 
              checked={settings.showWelcomeMessage}
              onChange={() => chatbotPublicId && updateSettings({ showWelcomeMessage: !settings.showWelcomeMessage }, chatbotPublicId)}
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="text-gray-300">Notification bubble</label>
            <Switch 
              checked={settings.showNotificationBubble}
              onChange={() => chatbotPublicId && updateSettings({ showNotificationBubble: !settings.showNotificationBubble }, chatbotPublicId)}
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="text-gray-300">Animations</label>
            <Switch 
              checked={settings.enableAnimation}
              onChange={() => chatbotPublicId && updateSettings({ enableAnimation: !settings.enableAnimation }, chatbotPublicId)}
            />
          </div>
        </div>
      </div>

      {/* Personnalisation */}
      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Customization</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
             Welcome message
            </label>
            <textarea
              value={welcomeMessage}
              onChange={handleWelcomeMessageChange}
              onBlur={handleWelcomeMessageBlur}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              rows={3}
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Custom icon
            </label>
            {settings.customIcon ? (
              <div className="relative w-16 h-16 rounded-lg overflow-hidden group">
                <img 
                  src={settings.customIcon} 
                  alt="Custom icon" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button
                    onClick={handleRemoveIcon}
                    className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                    disabled={loading}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <label className={`block w-16 h-16 rounded-lg border-2 border-dashed border-white/10 hover:border-indigo-500/50 transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleUploadIcon}
                  disabled={loading}
                />
                <div className="w-full h-full flex items-center justify-center">
                  <Upload className="w-6 h-6 text-gray-400" />
                </div>
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Script Configuration */}
      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Script Configuration</h3>
          <div className="px-2 py-1 bg-indigo-900/50 rounded-md text-xs text-indigo-300 flex items-center">
            <span className="inline-block w-2 h-2 bg-indigo-400 rounded-full mr-2"></span>
            Client-side configuration only
          </div>
        </div>
        
        <p className="text-gray-400 text-sm mb-4">
          These settings are not saved in the database, but are configured when integrating the widget on your website.
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Position
            </label>
            <select
              value={settings.position}
              onChange={(e) => updateScriptSettings({ position: e.target.value as 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={loading}
            >
              <option value="bottom-right">Bottom right</option>
              <option value="bottom-left">Bottom left</option>
              <option value="top-right">Top right</option>
              <option value="top-left">Top left</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                X Offset (px)
              </label>
              <input
                type="number"
                value={settings.offset.x}
                onChange={(e) => updateScriptSettings({ 
                  offset: { ...settings.offset, x: parseInt(e.target.value) } 
                })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={loading}
                min={0}
                max={100}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Y Offset (px)
              </label>
              <input
                type="number"
                value={settings.offset.y}
                onChange={(e) => updateScriptSettings({ 
                  offset: { ...settings.offset, y: parseInt(e.target.value) } 
                })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={loading}
                min={0}
                max={100}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Language
            </label>
            <select
              value={settings.lang}
              onChange={(e) => updateScriptSettings({ lang: e.target.value as 'fr' | 'en' | 'pt' })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={loading}
            >
              <option value="fr">French</option>
              <option value="en">English</option>
              <option value="pt">Portuguese</option>
            </select>
          </div>
        </div>
      </div>

      {/* Code d'intégration */}
      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Integration code</h3>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Simple version
            </label>
            <pre className="bg-black/50 rounded-lg p-4 overflow-x-auto">
              <code className="text-gray-300">{integrationCode}</code>
            </pre>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              Version with options
              <span className="px-2 py-0.5 bg-indigo-900/50 rounded-md text-xs text-indigo-300">Recommanded</span>
            </label>
            <p className="text-gray-400 text-sm mb-2">
              This version allows you to apply the settings you have configured in the "Script Configuration" section.
            </p>
            <pre className="bg-black/50 rounded-lg p-4 overflow-x-auto">
              <code className="text-gray-300">{integrationCodeWithOptions}</code>
            </pre>
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => navigator.clipboard.writeText(integrationCodeWithOptions)}
                className="text-xs px-3 py-1 bg-indigo-800/30 hover:bg-indigo-700/30 text-indigo-300 rounded-md transition-colors"
              >
                Copy code
              </button>
              <a 
                href={testPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-3 py-1 bg-green-800/30 hover:bg-green-700/30 text-green-300 rounded-md transition-colors"
              >
                Test widget
              </a>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Declarative version (HTML only)
            </label>
            <p className="text-gray-400 text-sm mb-2">
              Simpler integration with HTML attributes.
            </p>
            <pre className="bg-black/50 rounded-lg p-4 overflow-x-auto">
              <code className="text-gray-300">{declarativeCode}</code>
            </pre>
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => navigator.clipboard.writeText(declarativeCode)}
                className="text-xs px-3 py-1 bg-indigo-800/30 hover:bg-indigo-700/30 text-indigo-300 rounded-md transition-colors"
              >
                Copy code
              </button>
              <a 
                href={testPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-3 py-1 bg-green-800/30 hover:bg-green-700/30 text-green-300 rounded-md transition-colors"
              >
                Test widget
              </a>
            </div>
          </div>

          <div className="mt-6 bg-black/50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-300 mb-2">Need to check if your widget works?</h4>
            <p className="text-gray-400 text-sm mb-3">
              Use our test page to verify your widget is working correctly and try different configurations.
            </p>
            <a 
              href={testPageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-900/40 hover:bg-indigo-800/40 text-indigo-300 rounded-md transition-colors"
            >
              <span>Test my widget</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}